import {Menu as MenuIcon} from "iconoir-react";
import {useCallback, useEffect, useRef, useState} from 'react'
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Spinner from "react-bootstrap/Spinner";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import './SubLevelSettingsPopup.scss'
import {UserPropertiesDetails} from "../api/admin/generated";
import {EncryptionCertificates} from "../certificate/EncryptionCertificates";
import {SigningCertificate} from "../certificate/SigningCertificate";
import {RenderAlert} from "../common/RenderAlert";
import {SaveChangesRef} from "../common/SaveChangesRef"
import {Truncate} from "../common/Truncate";
import {DLPSelection} from "../dlp/DLPSelection";
import {useAvailablePropertiesContext} from "../hook/UseAvailablePropertiesContext";
import {useHasAllPermissions} from "../hook/UseHasAllPermissions";
import {PGPEncryptionKeys} from "../pgp/PGPEncryptionKeys";
import {PGPSigningKey} from "../pgp/PGPSigningKey";
import {SideBar} from "../sidebar/SideBar";
import {Utils} from "../utils/Utils";
import {SettingsMenuBuilder} from "./SettingsMenuBuilder";
import {SettingsLevel, SettingsLevelAndSubLevel, SettingsUtils} from "./SettingsUtils";
import {SettingsView} from "./SettingsView";

/**
 * Show the user settings on domain or user level. Which level is shown is determined by the URL path segments.
 *
 * The path params have the following structure
 *
 * path :== /domain/<sub-menu>/<settings-category>/<domain> | /user/<sub-menu>/<settings-category>/<email>
 *
 * <sub-menu> :== <letter> | <letter><sub-menu>
 * <settings-category> :== <category-name> | <category-name>/<settings-category>
 * <category-name> :== <letter> | <letter><category-name>
 *
 * For the user or domain level, the settings are shown on a modal popup. The modal popup contains a side bar menu
 * for the relevant settings for the domain or user. On smaller screens, the menu will be shown as an off canvas popup
 */
export const SubLevelSettingsPopup = () =>
{
    /**
     * The path segments have the following structure
     *
     * path :== /domain/<category>/<domain> | /user/<category>/<email>
     *
     * <category> :== <category-name> | <category-name>/<category>
     * <category-name> :== <letter> | <letter><category-name>
     */
    const location = useLocation()
    /**
     * All the available properties (system, domain and user level)
     */
    const {availableProperties} = useAvailablePropertiesContext();
    /**
     * A reference to a component with a saveSettings method which is used to save changes when the save button is clicked
     */
    const saveChangesRef = useRef<SaveChangesRef>();
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or if the settings
     * page is closed to the users or domains page
     */
    const navigate = useNavigate();
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * If set, settings should be saved
     */
    const [shouldSave, setShouldSave] = useState<boolean>(false);
    /**
     * Set to true while saving to show a spinner on the download button
     */
    const [saving, setSaving] = useState<boolean>(false);
    /**
     * Set to true if the mobile settings menu should be shown on small devices
     */
    const [showMobileSettingsMenu, setShowMobileSettingsMenu] = useState(false);
    /*
     * A helper for checking whether all required permissions are granted.
     */
    const hasAllPermissions = useHasAllPermissions();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();
    const {t:tu} = useTranslation('userproperties');

    /**
     * Listen for location changes and close the Offcanvas when the location changes
     */
    useEffect(() => {
        setShowMobileSettingsMenu(false);
    }, [location]);

    /**
     * The sub-menus
     */
    enum SettingsSubMenuEnum {
        Settings = "settings",
        SMIMEEncryption = "smime-encryption",
        SMIMESigning = "smime-signing",
        PGPEncryption = "pgp-encryption",
        PGPSigning = "pgp-signing",
        DLP = "dlp"
    }

    /**
     * Keeps track of the setting level and setting category (this is based on the URL)
     * Extracts the path from the params and splits the parts into a string array.
     *
     * Note: Access to the RAW URL parameters is required because an email address can contain a / character.
     * We therefore cannot use useParams() because this contains the URL decoded parameters
     */
    const getSettingsLevelAndSubLevel = useCallback(() : SettingsLevelAndSubLevel =>
    {
        const path = location.pathname
            .split("/")
            .map(s => decodeURIComponent(s))
            .filter(s => s.length > 0);

        return SettingsUtils.getSettingsLevelAndSubLevel(path);
    }, [location.pathname]);

    const settingsLevelAndSubLevel = getSettingsLevelAndSubLevel();

    if (settingsLevelAndSubLevel.settingsLevel === SettingsLevel.System) {
        throw new Error("System level is not valid for this component");
    }

    // settingsLevelSubSelector should be set for user and domain level
    if (settingsLevelAndSubLevel.settingsLevelSubSelector === undefined) {
        throw Error("settingsLevelSubSelector is undefined");
    }

    let userPropertiesDetails : UserPropertiesDetails[];

    switch (settingsLevelAndSubLevel.settingsLevel) {
        case SettingsLevel.User:
            userPropertiesDetails = availableProperties.userProperties;
            break;
        case SettingsLevel.Domain:
            userPropertiesDetails = availableProperties.domainProperties;
            break;
    }

    // note: we must clone settingsLevelAndSubLevel.categories because we cannot alter a state variable
    const settingsCategories = [...settingsLevelAndSubLevel.categories];

    const subMenuCategory = settingsCategories?.shift();

    if (subMenuCategory === undefined) {
        throw new Error("Sub menu category is undefined")
    }

    // get the sub menu (settings, encryption-certs etc)
    const settingsSubMenu = Utils.stringToEnum(SettingsSubMenuEnum, subMenuCategory as SettingsSubMenuEnum);

    function closePopup()
    {
        // redirect to user or domain
        navigate(settingsLevelAndSubLevel?.settingsLevel === SettingsLevel.Domain ? "/domains" : "/users")
    }

    /**
     * Returns a link to the sub menu target
     * @param settingsSubMenuEnum
     */
    function getMenuLink(settingsSubMenuEnum : SettingsSubMenuEnum) {
        return (settingsLevelAndSubLevel?.settingsLevel === SettingsLevel.Domain ? "/domains/" : "/users/") +
            settingsSubMenuEnum + "/" +
            encodeURIComponent(settingsLevelAndSubLevel?.settingsLevelSubSelector??"");
    }

    const renderSettingsMenu = () => {
        return (
            <SideBar className="sub-level-settings-menu">
                <SideBar.Dropdown title={t('settings.settings')}>
                    {SettingsMenuBuilder.buildMenu(userPropertiesDetails, "settings", tu,
                        settingsLevelAndSubLevel.settingsLevelSubSelector != undefined ?
                            [encodeURIComponent(settingsLevelAndSubLevel.settingsLevelSubSelector)] : [])}
                </SideBar.Dropdown>
                <SideBar.Dropdown title={t('settings.smime')}>
                    {hasEncryptionCertificatesPermissions() &&
                        <SideBar.Item  title={t('settings.encryption')} link={getMenuLink(SettingsSubMenuEnum.SMIMEEncryption)}/>
                    }
                    {hasSigningCertificatesPermissions() &&
                        <SideBar.Item title={t('settings.signing')}  link={getMenuLink(SettingsSubMenuEnum.SMIMESigning)}/>
                    }
                </SideBar.Dropdown>
                <SideBar.Dropdown title={t('settings.pgp')}>
                    {hasPGPEncryptionPermissions() &&
                        <SideBar.Item title={t('settings.encryption')} link={getMenuLink(SettingsSubMenuEnum.PGPEncryption)}/>
                    }
                    {hasPGPSigningPermissions() &&
                        <SideBar.Item title={t('settings.signing')} link={getMenuLink(SettingsSubMenuEnum.PGPSigning)}/>
                    }
                </SideBar.Dropdown>
                {hasDLPPermissions() &&
                    <SideBar.Item title={t('settings.dlp')} link={getMenuLink(SettingsSubMenuEnum.DLP)}/>
                }
            </SideBar>
        )
    }

    const hasEncryptionCertificatesPermissions= ()=>
    {
        switch (settingsLevelAndSubLevel.settingsLevel)
        {
            case SettingsLevel.User: return hasAllPermissions(
                [
                    "certificate:search-for-certificates-by-email-count",
                    "certificate:search-for-certificates-by-email",
                    "certificate-validation:validate-certificate-for-encryption",
                    "certificate:get-certificates-count",
                    "certificate:get-certificates",
                    "certificate-selection:get-auto-selected-encryption-certificates-for-user",
                    "certificate-selection:get-explicit-certificates-for-user",
                    "certificate-selection:get-inherited-certificates-for-user"
                ]
            );

            case SettingsLevel.Domain: return hasAllPermissions(
                [
                    "certificate:search-for-certificates-by-email-count",
                    "certificate:search-for-certificates-by-email",
                    "certificate-validation:validate-certificate-for-encryption",
                    "certificate:get-certificates-count",
                    "certificate:get-certificates",
                    "certificate-selection:get-explicit-certificates-for-domain",
                    "certificate-selection:get-inherited-certificates-for-domain",
            ]);
        }

        return false;
    }

    const hasSigningCertificatesPermissions= ()=>
    {
        switch (settingsLevelAndSubLevel.settingsLevel)
        {
            case SettingsLevel.User: return hasAllPermissions(
                [
                    "certificate:search-for-certificates-by-email-count",
                    "certificate:search-for-certificates-by-email",
                    "certificate-validation:validate-certificate-for-signing",
                    "certificate:get-certificates-count",
                    "certificate:get-certificates",
                    "certificate-selection:get-signing-certificate-for-user"
                ]);

            case SettingsLevel.Domain: return hasAllPermissions(
                [
                    "certificate:search-for-certificates-by-email-count",
                    "certificate:search-for-certificates-by-email",
                    "certificate-validation:validate-certificate-for-signing",
                    "certificate:get-certificates-count",
                    "certificate:get-certificates",
                    "certificate-selection:get-signing-certificate-for-domain",
                ]);
        }

        return false;
    }

    const hasPGPEncryptionPermissions= ()=>
    {
        switch (settingsLevelAndSubLevel.settingsLevel) {
            case SettingsLevel.User: return hasAllPermissions(["pgp:get-encryption-keys-for-email"]);

            case SettingsLevel.Domain: return hasAllPermissions(["pgp:get-encryption-keys-for-domain"]);
        }

        return false;
    }

    const hasPGPSigningPermissions= ()=>
    {
        switch (settingsLevelAndSubLevel.settingsLevel) {
            case SettingsLevel.User: return hasAllPermissions(
                [
                    "pgp:get-signing-key-for-user",
                    "pgp:available-signing-keys-for-email",
                    "pgp:is-user-id-revoked",
                    "pgp:is-user-id-valid"
                ]);

            case SettingsLevel.Domain: return hasAllPermissions(
                [
                    "pgp:get-signing-key-for-domain",
                    "pgp:get-available-signing-keys-for-domain",
                    "pgp:is-user-id-revoked",
                    "pgp:is-user-id-valid"
                ]);
        }

        return false;
    }

    const hasDLPPermissions= ()=>
    {
        switch (settingsLevelAndSubLevel.settingsLevel) {
            case SettingsLevel.User: return hasAllPermissions(
                [
                    "dlp:get-policy-patterns-count",
                    "dlp:get-policy-patterns",
                    "dlp:get-user-policy-patterns"
                ]);

            case SettingsLevel.Domain: return hasAllPermissions(
                [
                    "dlp:get-policy-patterns-count",
                    "dlp:get-policy-patterns",
                    "dlp:get-domain-policy-patterns"
                ]);
        }

        return false;
    }

    return (
        <Modal
            size="xl"
            fullscreen="lg-down"
            aria-labelledby="contained-modal-title-vcenter"
            animation={false}
            show={true}
            onHide={() => closePopup()}
            centered
            backdrop="static"
        >
            <Modal.Header closeButton>
                {/* menu button only shown on small devices which actives the offline-canvas menu */}
                <Button variant="primary" className="d-lg-none" onClick={() => setShowMobileSettingsMenu(true)}>
                    <MenuIcon height="1em" width="1em"/>
                </Button>

                {/* off-canvas menu which will be shown on small devices */}
                <Offcanvas show={showMobileSettingsMenu} onHide={() => setShowMobileSettingsMenu(false)} className="mobile-sidebar-offcanvas">
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title>{t("settings.settings-for")} {settingsLevelAndSubLevel.settingsLevelSubSelector}</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        {renderSettingsMenu()}
                    </Offcanvas.Body>
                </Offcanvas>

                <Modal.Title className="text-break text-secondary">
                    <Truncate maxLength={80} value={t("settings.settings-for")  + ` ${settingsLevelAndSubLevel.settingsLevelSubSelector}`} placement="bottom" />
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="row flex-grow-1">
                    <div className="sidebar col-2 bg-body-tertiary d-flex flex-column overflow-auto modal-side-bar border-end d-none d-lg-flex vh-100">
                        {renderSettingsMenu()}
                    </div>

                    <main className="col d-flex flex-column overflow-auto modal-main-body">
                        {settingsSubMenu === SettingsSubMenuEnum.Settings &&
                            <SettingsView settingsLevel={settingsLevelAndSubLevel.settingsLevel}
                                          settingsLevelSubSelector={settingsLevelAndSubLevel.settingsLevelSubSelector}
                                          categories={settingsCategories.join("/")}
                                          setErrorMessage={setErrorMessage}
                                          setShouldSave={setShouldSave}
                                          ref={saveChangesRef}
                                          setSaving={setSaving}
                            />
                        }
                        {settingsSubMenu === SettingsSubMenuEnum.SMIMEEncryption &&
                            <EncryptionCertificates settingsLevel={settingsLevelAndSubLevel.settingsLevel}
                                                    settingsLevelSubSelector={settingsLevelAndSubLevel.settingsLevelSubSelector}
                                                    setShouldSave={setShouldSave}
                                                    ref={saveChangesRef}
                                                    setSaving={setSaving}
                            />
                        }
                        {settingsSubMenu === SettingsSubMenuEnum.SMIMESigning &&
                            <SigningCertificate settingsLevel={settingsLevelAndSubLevel.settingsLevel}
                                                settingsLevelSubSelector={settingsLevelAndSubLevel.settingsLevelSubSelector}
                                                setShouldSave={setShouldSave}
                                                ref={saveChangesRef}
                                                setSaving={setSaving}
                            />
                        }
                        {settingsSubMenu === SettingsSubMenuEnum.PGPEncryption &&
                            <PGPEncryptionKeys settingsLevel={settingsLevelAndSubLevel.settingsLevel}
                                               settingsLevelSubSelector={settingsLevelAndSubLevel.settingsLevelSubSelector}
                            />
                        }
                        {settingsSubMenu === SettingsSubMenuEnum.PGPSigning &&
                            <PGPSigningKey settingsLevel={settingsLevelAndSubLevel.settingsLevel}
                                           settingsLevelSubSelector={settingsLevelAndSubLevel.settingsLevelSubSelector}
                                           setShouldSave={setShouldSave}
                                           ref={saveChangesRef}
                                           setSaving={setSaving}
                            />
                        }
                        {settingsSubMenu === SettingsSubMenuEnum.DLP &&
                            <DLPSelection settingsLevel={settingsLevelAndSubLevel.settingsLevel}
                                          settingsLevelSubSelector={settingsLevelAndSubLevel.settingsLevelSubSelector}
                                          setShouldSave={setShouldSave}
                                          ref={saveChangesRef}
                                          setSaving={setSaving}
                            />
                        }
                    </main>
                </div>
            </Modal.Body>

            <Modal.Footer>
                {/* show an alert if an error occurred saving the settings */}
                <RenderAlert message={errorMessage} />

                <Button variant="success" type="submit" onClick={() => saveChangesRef.current?.saveChanges()}
                        disabled={!shouldSave}>
                    {saving && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {saving ? t("common.saving") : t("common.save")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
